import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./RoleEdit.css";
import privateApi from "../../apis/privateApi";
import Spinner from "../../components/Spinner/Spinner";
import Notification from "../../components/Notification/Notification";

const RoleEdit = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const roleId = state?.role?._id;

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [permissions, setPermissions] = useState([]);
    const [allowedBudgetLevels, setAllowedBudgetLevels] = useState([]);
    const [level, setLevel] = useState(1);

    const [permissionsList, setPermissionsList] = useState([]);
    const [budgetLevels, setBudgetLevels] = useState([]);

    const [loading, setLoading] = useState(false);
    const [metaLoading, setMetaLoading] = useState(true);

    const [notification, setNotification] = useState({
        show: false,
        message: "",
        type: "info",
    });

    const handleCloseNotification = () => {
        setNotification({ ...notification, show: false });
    };

    const toggleSelection = (value, list, setList) => {
        if (list.includes(value)) {
            setList(list.filter((item) => item !== value));
        } else {
            setList([...list, value]);
        }
    };

    const formatLabel = (value) =>
        value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

    useEffect(() => {
        if (!roleId) {
            setNotification({
                show: true,
                message: "Role ID is missing.",
                type: "error",
            });
            return;
        }

        const fetchData = async () => {
            try {
                const metaRes = await privateApi.get("/api/roles/meta");
                setPermissionsList(metaRes.data.permissions || []);
                setBudgetLevels(metaRes.data.allowedBudgetLevels || []);

                const roleRes = await privateApi.get(`/api/roles/${roleId}`);
                const role = roleRes.data.data;

                setName(role.name || "");
                setDescription(role.description || "");
                setPermissions(role.permissions || []);
                setAllowedBudgetLevels(role.allowedBudgetLevels || []);
                setLevel(role.level || 1);
            } catch (err) {
                console.error(err);
                setNotification({
                    show: true,
                    message: "Failed to load role data.",
                    type: "error",
                });
            } finally {
                setMetaLoading(false);
            }
        };

        fetchData();
    }, [roleId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setNotification({ show: false, message: "", type: "info" });

        try {
            const res = await privateApi.patch(`/api/roles/${roleId}`, {
                name,
                description,
                permissions,
                allowedBudgetLevels,
                level,
            });

            setNotification({
                show: true,
                message: res.data.message || "Role updated successfully!",
                type: "success",
            });

            setTimeout(() => navigate("/roles"), 1000);
        } catch (err) {
            console.error(err);
            setNotification({
                show: true,
                message:
                    err?.response?.data?.message || "Failed to update role.",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    if (metaLoading) {
        return (
            <div className="role-edit-container">
                <Spinner size={40} height={4} color="#008753" />
            </div>
        );
    }

    return (
        <div className="role-edit-container">
            <div className="role-edit-card">
                <button
                    type="button"
                    className="role-edit-back-button"
                    onClick={() => navigate(-1)}
                >
                    ← Back
                </button>

                <h2>Edit Role</h2>

                <Notification
                    show={notification.show}
                    message={notification.message}
                    type={notification.type}
                    onClose={handleCloseNotification}
                />

                <form onSubmit={handleSubmit} className="role-edit-form">
                    <label className="role-edit-form-label">Role Name</label>
                    <input
                        type="text"
                        placeholder="Enter role name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                    />

                    <label className="role-edit-form-label">Description</label>
                    <textarea
                        placeholder="Enter description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />

                    <label className="role-edit-form-label">Permissions</label>
                    <div className="role-edit-checkbox-group">
                        {permissionsList.map((perm) => (
                            <label key={perm} className="role-edit-checkbox-item">
                                <input
                                    type="checkbox"
                                    checked={permissions.includes(perm)}
                                    onChange={() =>
                                        toggleSelection(perm, permissions, setPermissions)
                                    }
                                />
                                {formatLabel(perm)}
                            </label>
                        ))}
                    </div>

                    <label className="role-edit-form-label">Allowed Budget Levels</label>
                    <div className="role-edit-checkbox-group">
                        {budgetLevels.map((lvl) => (
                            <label key={lvl} className="role-edit-checkbox-item">
                                <input
                                    type="checkbox"
                                    checked={allowedBudgetLevels.includes(lvl)}
                                    onChange={() =>
                                        toggleSelection(
                                            lvl,
                                            allowedBudgetLevels,
                                            setAllowedBudgetLevels
                                        )
                                    }
                                />
                                {formatLabel(lvl)}
                            </label>
                        ))}
                    </div>

                    <label className="role-edit-form-label">Role Level</label>
                    <input
                        type="number"
                        min="1"
                        value={level}
                        onChange={(e) => setLevel(e.target.value)}
                    />

                    <button type="submit" disabled={loading}>
                        {loading ? (
                            <Spinner size={20} height={3} color="#ffffff" />
                        ) : (
                            "Update Role"
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default RoleEdit;